import React, { useState } from 'react'
import { Button, Modal, Progress, Stack, Text } from '@mantine/core'
import { IconCheck, IconPrinter, IconX } from '@tabler/icons-react'
import { generateReportImageDataUrl } from '../utils/imageGenerator'
import { ThemeConfig } from '../utils/themeConfig'

interface ImagePrintButtonProps {
  reportTitle?: string
  className?: string
  themeConfig: ThemeConfig
}

export default function ImagePrintButton({ reportTitle, className, themeConfig }: ImagePrintButtonProps) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const printImage = (imageDataUrl: string) => {
    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.top = '0'
    iframe.style.left = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = 'none'
    iframe.style.opacity = '0'
    document.body.appendChild(iframe)

    const iframeDoc = iframe.contentWindow?.document
    if (!iframeDoc) {
      document.body.removeChild(iframe)
      throw new Error('无法创建打印窗口，请检查浏览器设置')
    }

    iframeDoc.open()
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${reportTitle || '风险评估报告'}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            @page {
              size: A4;
              margin: 15mm;
            }

            @media print {
              html, body {
                width: 210mm;
                margin: 0;
                padding: 0;
              }
            }

            body {
              margin: 0;
              padding: 0;
              background: white;
            }

            img {
              width: 100%;
              height: auto;
              display: block;
            }
          </style>
        </head>
        <body>
          <img
            src="${imageDataUrl}"
            alt="${reportTitle || '风险评估报告'}"
          />
        </body>
      </html>
    `)
    iframeDoc.close()

    const img = iframeDoc.querySelector('img')
    if (img) {
      img.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.print()

          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe)
            }
          }, 1000)
        }, 100)
      }

      img.onerror = () => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe)
        }
        throw new Error('图片加载失败')
      }
    }
  }

  const handlePrintImage = async () => {
    setIsGenerating(true)
    setShowModal(true)
    setProgress(0)
    setProgressMessage('准备生成图片...')
    setError(null)

    try {
      const imageDataUrl = await generateReportImageDataUrl('report', {
        onProgress: (prog, msg) => {
          if (prog < 90) {
            setProgress(prog)
            setProgressMessage(msg)
          } else if (prog === 100) {
            setProgress(90)
            setProgressMessage('准备打印预览...')
          }
        },
        quality: 1,
        scale: 2,
      })

      printImage(imageDataUrl)

      setProgress(100)
      setProgressMessage('打印对话框已打开')

      setTimeout(() => {
        setShowModal(false)
        setIsGenerating(false)
      }, 800)
    } catch (err) {
      console.error('打印准备失败:', err)
      setError(err instanceof Error ? err.message : '准备打印时发生未知错误')
      setIsGenerating(false)
    }
  }

  return (
    <>
      <div
        className={`flex flex-col items-center justify-center cursor-pointer p-3 transition-all duration-300 hover:scale-105 ${className || ''}`}
        onClick={isGenerating ? undefined : handlePrintImage}
        style={{
          backgroundColor: themeConfig.backgroundColor,
          opacity: isGenerating ? 0.6 : 1,
          cursor: isGenerating ? 'not-allowed' : 'pointer',
        }}
      >
        <IconPrinter size={28} color={themeConfig.primaryColor} />
        <span className="text-gray-500 text-xs font-medium mt-1">打印报告</span>
      </div>

      <Modal
        opened={showModal}
        onClose={() => !isGenerating && setShowModal(false)}
        title="打印报告"
        centered
        closeOnClickOutside={!isGenerating}
        closeOnEscape={!isGenerating}
        withCloseButton={!isGenerating}
      >
        <Stack gap="md">
          {error ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#ef4444' }}>
                <IconX size={48} />
                <Text mt="md" fw={500}>
                  打印准备失败
                </Text>
                <Text size="sm" c="dimmed" mt="xs">
                  {error}
                </Text>
              </div>
              <Button fullWidth onClick={() => setShowModal(false)} color="red">
                关闭
              </Button>
            </>
          ) : progress === 100 ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#22c55e' }}>
                <IconCheck size={48} />
                <Text mt="md" fw={500}>
                  准备完成
                </Text>
                <Text size="sm" c="dimmed" mt="xs">
                  请在打印对话框中确认打印
                </Text>
              </div>
            </>
          ) : (
            <>
              <Progress value={progress} size="lg" radius="md" striped animated color={themeConfig.primaryColor} />
              <Text size="sm" c="dimmed" ta="center">
                {progressMessage} ({Math.round(progress)}%)
              </Text>
              <Text size="xs" c="dimmed" ta="center">
                请稍候，正在准备打印内容...
              </Text>
            </>
          )}
        </Stack>
      </Modal>
    </>
  )
}
